import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import usePersistentState from 'shared/hooks/usePersistentState';
import { LoadingSpinner, Modal } from 'components/basicos';
import PedidosPanel from './PedidosPanel';
import PedidosHistorial from './PedidosHistorial';
import PedidoDetalle from 'components/Chat/PedidoDetalle';
import ArmadorPanel from './ArmadorPanel';
import DeliveryPanel from './DeliveryPanel';
import useTrabajadorData from './useTrabajadorData';
import useTrabajadorSocket from './useTrabajadorSocket';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import s from './styles';
import API from 'shared/api';
import {
  FiPackage,
  FiArrowLeft,
  FiTruck,
  FiBox,
  FiAlertTriangle,
  FiCoffee,
  FiClock,
} from 'react-icons/fi';

const PANELES = [
  {
    id: 'pedidos',
    label: 'Pedidos',
    icono: <FiPackage size={15} />,
    acceso: 'chat.acceder',
    gestion: 'pedidos.gestionar',
    areas: ['panel', 'revision', 'verificacion'],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    icono: <FiTruck size={15} />,
    acceso: 'pedidos.delivery',
    gestion: 'pedidos.delivery_gestionar',
    areas: ['delivery'],
  },
  {
    id: 'armado',
    label: 'Armado',
    icono: <FiBox size={15} />,
    acceso: 'pedidos.armado',
    gestion: 'pedidos.armado_gestionar',
    areas: ['armado'],
  },
  {
    id: 'cocina',
    label: 'Cocina',
    icono: <FiCoffee size={15} />,
    acceso: 'pedidos.cocinero',
    gestion: 'pedidos.cocinero_gestionar',
    areas: ['cocina'],
  },
  {
    id: 'historial',
    label: 'Historial',
    icono: <FiClock size={15} />,
    acceso: 'pedidos.delivery',
    gestion: null,
    areas: ['historial'],
  },
];

const TrabajadorPanel = () => {
  const { usuario, isAdmin, getSedeInfo, tienePermiso, isTipoEmpresa } = useAuth();
  const esImportador = isTipoEmpresa('importador');
  const { noLeidasPorArea, marcarLeidasPorArea } = useNotificaciones();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const sedeInfo = getSedeInfo();

  const pendientesPorPanel = (p) =>
    (p.areas || []).reduce((acc, a) => acc + (noLeidasPorArea[a] || 0), 0);

  const seleccionarTab = (p) => {
    setActiveTab(p.id);
    setPedidoActivo(null);
    setPedidoAbiertoId(null);
    marcarLeidasPorArea(p.areas || []);
  };

  const panelesVisibles = PANELES.filter((p) => {
    // Historial: visible tambien para quien gestiona pedidos en empresa importadora,
    // no solo para quien tiene panel de delivery.
    if (p.id === 'historial') {
      if (tienePermiso(p.acceso)) return true;
      if (esImportador && tienePermiso('pedidos.gestionar')) return true;
      return false;
    }
    if (!tienePermiso(p.acceso)) return false;
    return true;
  });
  const tienePanelesEspeciales = panelesVisibles.some((p) => p.id !== 'pedidos');
  const defaultTab = tienePanelesEspeciales
    ? panelesVisibles.find((p) => p.id !== 'pedidos')?.id
    : 'pedidos';
  const [activeTab, setActiveTab] = usePersistentState(
    'agorax_worker_tab',
    defaultTab || panelesVisibles[0]?.id || 'pedidos',
    { validate: (v) => panelesVisibles.some((p) => p.id === v) }
  );

  const [sedeFilter, setSedeFilter] = usePersistentState(
    'agorax_worker_sede',
    isAdmin() ? '' : sedeInfo?.codigo || ''
  );
  const [pedidoActivo, setPedidoActivo] = useState(null);
  const [pedidoAbiertoId, setPedidoAbiertoId] = usePersistentState('agorax_worker_pedido', null);
  const pedidoRehidratadoRef = useRef(false);
  const [chatActivo, setChatActivo] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);
  const mostrarToast = useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 3500);
  }, []);

  const recargaTimeoutRef = useRef(null);
  const chatPedidoRef = useRef(null);
  const pedidoActivoRef = useRef(null);
  const pedidosConNoLeidosRef = useRef([]);
  pedidoActivoRef.current = pedidoActivo;

  const {
    conversacionesBot,
    conversacionesAsesor,
    mensajes,
    pedidos,
    sedes,
    cargando,
    cargandoMas,
    hasMore,
    cargarConversacionesBot,
    cargarConversacionesAsesor,
    cargarMensajes,
    cargarMasMensajes,
    recargarMensajes,
    marcarMensajesComoLeidos,
    enviarMensaje,
    cargarClientesPendientes,
    cargarPedidos,
    tomarChat,
    liberarChat,
    inicializar,
  } = useTrabajadorData();

  useEffect(() => {
    inicializar(sedeFilter, isAdmin());
    // eslint-disable-next-line
  }, [sedeFilter]);

  const recargarListas = useCallback(() => {
    if (recargaTimeoutRef.current) clearTimeout(recargaTimeoutRef.current);
    recargaTimeoutRef.current = setTimeout(() => {
      cargarPedidos(sedeFilter);
      cargarConversacionesBot(sedeFilter);
      cargarConversacionesAsesor(sedeFilter);
      cargarClientesPendientes(sedeFilter);
    }, 200);
  }, [
    sedeFilter,
    cargarPedidos,
    cargarConversacionesBot,
    cargarConversacionesAsesor,
    cargarClientesPendientes,
  ]);

  // Red de seguridad: recargar las listas cada 30s aunque el socket falle (reconexion,
  // evento perdido, cliente sin empresa_id, etc.). No reemplaza al socket, solo lo respalda.
  useEffect(() => {
    const id = setInterval(() => {
      recargarListas();
    }, 30000);
    return () => clearInterval(id);
  }, [recargarListas]);

  const handlePedidoActualizado = useCallback(
    (data) => {
      recargarListas();
      const abierto = pedidoActivoRef.current;
      if (abierto && data.pedido_id && parseInt(data.pedido_id) === parseInt(abierto.id)) {
        let productosActualizados = false;
        setPedidoActivo((prev) => {
          if (!prev) return null;
          let changed = false;
          const updated = { ...prev };
          if (
            data.estado &&
            data.estado !== 'productos_actualizados' &&
            data.estado !== prev.estado
          ) {
            updated.estado = data.estado;
            changed = true;
          }
          if (data.productos && Array.isArray(data.productos)) {
            updated.productos = data.productos;
            changed = true;
            productosActualizados = true;
          }
          if (data.total !== undefined && data.total !== prev.total) {
            updated.total = data.total;
            changed = true;
          }
          if (
            data.confirmacion_pendiente !== undefined &&
            data.confirmacion_pendiente !== prev.confirmacion_pendiente
          ) {
            updated.confirmacion_pendiente = data.confirmacion_pendiente;
            changed = true;
          }
          return changed ? updated : prev;
        });
        if (productosActualizados) {
          mostrarToast(`El cliente modifico el pedido #${data.pedido_id}`);
        }
      }
    },
    [recargarListas, mostrarToast]
  );

  const handleNuevoMensajeSocket = useCallback(
    async (data) => {
      const pedidoCliente = pedidosConNoLeidosRef.current.find(
        (x) => parseInt(x.idcliente) === parseInt(data.idcliente)
      );
      if (data.autor === 'cliente' && pedidoCliente?.estado !== 'pendiente') {
        const msgAtendidoPor = data.atendido_por || pedidoCliente?.atendido_por;
        const yoAtiendo = msgAtendidoPor === usuario?.cedula;
        const nadieAtiende = !msgAtendidoPor;
        if (yoAtiendo || nadieAtiende) {
          const nombre = pedidoCliente?.cliente || pedidoCliente?.nombre || 'desconocido';
          mostrarToast(`Nuevo mensaje de ${nombre}`);
        }
      }
      recargarListas();
      const p = chatPedidoRef.current;
      if (p && parseInt(data.idcliente) === parseInt(p.idcliente)) {
        await recargarMensajes(p.idcliente, p.id);
        await marcarMensajesComoLeidos(p.idcliente, p.id);
      }
    },
    [recargarListas, recargarMensajes, mostrarToast, marcarMensajesComoLeidos, usuario]
  );

  const handleCartUpdate = useCallback(
    (data) => {
      recargarListas();
      const abierto = pedidoActivoRef.current;
      if (
        abierto &&
        data &&
        data.session_id &&
        String(data.session_id) === String(abierto.session_id)
      ) {
        setPedidoActivo((prev) => {
          if (!prev) return null;
          const nuevosProductos = data.carrito || data.productos;
          if (nuevosProductos && Array.isArray(nuevosProductos) && nuevosProductos.length > 0) {
            return { ...prev, productos: nuevosProductos };
          }
          return prev;
        });
      }
    },
    [recargarListas]
  );

  const handleChatTomado = useCallback(
    async (data) => {
      recargarListas();
      const abierto = pedidoActivoRef.current;
      if (abierto && data.idcliente && parseInt(data.idcliente) === parseInt(abierto.idcliente)) {
        setPedidoActivo((prev) => prev ? {
          ...prev,
          atendido_por: data.atendido_por || '',
          nombre_asesor: data.nombre_asesor || '',
        } : null);
      }
    },
    [recargarListas]
  );

  const handleChatLiberado = useCallback((data) => {
    recargarListas();
    const abierto = pedidoActivoRef.current;
    if (abierto && data.idcliente && parseInt(data.idcliente) === parseInt(abierto.idcliente)) {
      setPedidoActivo((prev) => prev ? {
        ...prev,
        atendido_por: '',
        nombre_asesor: '',
      } : null);
    }
  }, [recargarListas]);

  const handleAsesorSolicitado = useCallback(
    (data) => {
      const nombre = data.nombre_cliente || `#${data.idcliente || '?'}`;
      mostrarToast(`${nombre} solicita un asesor`);
      recargarListas();
    },
    [mostrarToast, recargarListas]
  );

  useTrabajadorSocket({
    onNuevoMensaje: handleNuevoMensajeSocket,
    onPedidoActualizado: handlePedidoActualizado,
    onChatTomado: handleChatTomado,
    onCartUpdate: handleCartUpdate,
    onChatLiberado: handleChatLiberado,
    onAsesorSolicitado: handleAsesorSolicitado,
  });

  const seleccionarPedido = async (pedido) => {
    setChatActivo(false);
    try {
      const res = await API.get(`/pedidos/${pedido.id}`);
      if (res.data.status === 'success') {
        setPedidoActivo(res.data.data);
        setPedidoAbiertoId(res.data.data.id);
        return;
      }
    } catch {
      /* fallback to list data */
    }
    setPedidoActivo(pedido);
    setPedidoAbiertoId(pedido.id);
  };

  const cerrarDetalle = () => {
    setPedidoActivo(null);
    setPedidoAbiertoId(null);
    setChatActivo(false);
  };

  // Rehidratar el pedido abierto tras una recarga (persistencia de navegacion)
  useEffect(() => {
    if (pedidoRehidratadoRef.current) return;
    pedidoRehidratadoRef.current = true;
    if (!pedidoAbiertoId) return;
    API.get(`/pedidos/${pedidoAbiertoId}`)
      .then((res) => {
        if (res.data.status === 'success') setPedidoActivo(res.data.data);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoAbiertoId]);

  const handleAbrirChatEnDetalle = useCallback(
    async (pedido) => {
      chatPedidoRef.current = pedido;
      await cargarMensajes(pedido.idcliente, pedido.id, true);
      await marcarMensajesComoLeidos(pedido.idcliente, pedido.id);
      recargarListas();
    },
    [cargarMensajes, marcarMensajesComoLeidos, recargarListas]
  );

  const handleEnviarMensajeChat = useCallback(
    async (idcliente, texto, pedidoId, respuestaAId = null) => {
      const result = await enviarMensaje(idcliente, texto, pedidoId, respuestaAId);
      if (result.success) {
        await recargarMensajes(idcliente, pedidoId);
        recargarListas();
      }
    },
    [enviarMensaje, recargarMensajes, recargarListas]
  );

  const handleChatRefresh = useCallback(
    async (idcliente, pedidoId) => {
      await recargarMensajes(idcliente, pedidoId);
      recargarListas();
    },
    [recargarMensajes, recargarListas]
  );

  const pedidosConNoLeidos = (pedidos || []).map((p) => {
    const conv = [...conversacionesBot, ...conversacionesAsesor].find(
      (c) => parseInt(c.id) === parseInt(p.id)
    );
    return { ...p, noLeidos: conv?.noLeidos || 0 };
  });
  pedidosConNoLeidosRef.current = pedidosConNoLeidos;

  const puedeGestionarPedidos = tienePermiso('pedidos.gestionar');
  const puedeCrearPedido = puedeGestionarPedidos || tienePermiso('vendedores.pedido.crear');
  const puedeEditarPedido = puedeGestionarPedidos || tienePermiso('vendedores.pedido.editar');
  const puedeGestionarDelivery = tienePermiso('pedidos.delivery_gestionar');
  const puedeGestionarArmado = tienePermiso('pedidos.armado_gestionar');
  const cocineroGestionar = tienePermiso('pedidos.cocinero_gestionar');

  if (cargando) return <LoadingSpinner mensaje="Cargando panel..." />;

  // ── MOBILE ──
  if (isMobile) {
    const pedidosKanban = activeTab === 'pedidos' && !pedidoActivo;
    const pedidosDetalle = activeTab === 'pedidos' && pedidoActivo;
    const mostrarTabs = pedidosKanban || !pedidoActivo;

    return (
      <div style={s.container}>
        <style>{`
          @media (max-width: 768px) {
            .tp-tab-btn { padding: 10px 8px !important; font-size: 11px !important; }
            .tp-dash-badge { font-size: 10px !important; padding: 3px 7px !important; }
            .tp-dashboard { padding: 4px 8px !important; gap: 4px !important; }
            .tp-tab-group-mobile { overflow-x: auto; -webkit-overflow-scrolling: touch; flex-wrap: nowrap !important; gap: 2px !important; }
          }
          @media (max-width: 380px) {
            .tp-tab-label { display: none; }
            .tp-tab-btn { padding: 10px 6px !important; }
          }
        `}</style>
        <div style={s.tabsBar}>
          <div className="tp-tab-group-mobile" style={s.tabGroup}>
            {pedidosDetalle && (
              <button
                onClick={cerrarDetalle}
                style={{
                  ...s.tabBtn,
                  color: 'var(--agx-pri2)',
                  fontWeight: 600,
                  padding: '14px 12px',
                  flexShrink: 0,
                }}
              >
                <FiArrowLeft size={16} /> Volver
              </button>
            )}
            {mostrarTabs &&
              panelesVisibles.map((p) => (
                <button
                  key={p.id}
                  onClick={() => seleccionarTab(p)}
                  style={{
                    ...s.tabBtn,
                    ...(activeTab === p.id ? s.tabBtnActive : {}),
                    flexShrink: 0,
                  }}
                >
                  {p.icono} <span className="tp-tab-label">{p.label}</span>
                  {pendientesPorPanel(p) > 0 && (
                    <span style={s.tabBadge}>
                      {pendientesPorPanel(p) > 9 ? '9+' : pendientesPorPanel(p)}
                    </span>
                  )}
                </button>
              ))}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {pedidosKanban && (
              <PedidosPanel
                pedidos={pedidosConNoLeidos}
                pedidoActivo={pedidoActivo}
                onSelectPedido={seleccionarPedido}
                sedeFilter={sedeFilter}
                onSedeFilterChange={setSedeFilter}
                sedes={sedes}
                puedeGestionar={puedeGestionarPedidos}
                puedeCrear={puedeCrearPedido}
              />
            )}
            {pedidosDetalle && pedidoActivo && (
            <PedidoDetalle
              pedido={pedidoActivo}
              onBack={cerrarDetalle}
              readOnly={!puedeEditarPedido}
              isMobile
              chatMensajes={mensajes}
              chatCargandoMas={cargandoMas}
              chatHasMore={hasMore}
              onChatCargarMas={cargarMasMensajes}
              onChatEnviar={handleEnviarMensajeChat}
              onChatAbrir={handleAbrirChatEnDetalle}
              onChatToggle={setChatActivo}
              onTomarChat={tomarChat}
              onLiberarChat={isAdmin() ? liberarChat : undefined}
              onChatRefresh={handleChatRefresh}
            />
          )}
          {activeTab === 'delivery' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!puedeGestionarDelivery && (
                <div
                  style={{
                    padding: '10px 16px',
                    margin: '12px 12px 0',
                    borderRadius: '10px',
                    background: 'var(--agx-amber-bg)',
                    border: '1px solid var(--agx-amber)',
                    color: 'var(--agx-warning-text)',
                    fontSize: '12px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
                  entregas.
                </div>
              )}
              <DeliveryPanel
                isReadOnly={!puedeGestionarDelivery}
                sedeFilter={sedeFilter}
                isAdmin={isAdmin()}
                deliveryCedula={usuario?.cedula}
              />
            </div>
          )}
          {activeTab === 'armado' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!puedeGestionarArmado && (
                <div
                  style={{
                    padding: '10px 16px',
                    margin: '12px 12px 0',
                    borderRadius: '10px',
                    background: 'var(--agx-amber-bg)',
                    border: '1px solid var(--agx-amber)',
                    color: 'var(--agx-warning-text)',
                    fontSize: '12px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
                  armado.
                </div>
              )}
              <ArmadorPanel
                isReadOnly={!puedeGestionarArmado}
                sedeFilter={sedeFilter}
                armadorCedula={usuario?.cedula}
                esCocinero={false}
                cocineroGestionar={false}
              />
            </div>
          )}
          {activeTab === 'cocina' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!cocineroGestionar && (
                <div
                  style={{
                    padding: '10px 16px',
                    margin: '12px 12px 0',
                    borderRadius: '10px',
                    background: 'var(--agx-amber-bg)',
                    border: '1px solid var(--agx-amber)',
                    color: 'var(--agx-warning-text)',
                    fontSize: '12px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
                  cocina.
                </div>
              )}
              <ArmadorPanel
                isReadOnly={!cocineroGestionar}
                sedeFilter={sedeFilter}
                armadorCedula={usuario?.cedula}
                esCocinero={true}
                cocineroGestionar={cocineroGestionar}
              />
            </div>
          )}
          {activeTab === 'historial' && <PedidosHistorial sedeFilter={sedeFilter} />}
        </div>
        <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        {toastMsg && (
          <div
            style={{
              position: 'fixed',
              bottom: '28px',
              right: '28px',
              padding: '14px 24px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              zIndex: 2000,
              animation: 'toastIn 0.3s ease',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              background: 'var(--agx-pri)',
            }}
          >
            {toastMsg}
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP ──
  return (
    <div style={s.container}>
      <style>{`
        @media (max-width: 768px) {
          .tp-tab-btn { padding: 12px 10px !important; font-size: 12px !important; }
          .tp-sede-select { min-width: 120px !important; max-width: 160px !important; font-size: 11px !important; }
          .tp-tab:not(.tp-tab-active) { display: none !important; }
          .tp-dash-badge { font-size: 10px !important; padding: 3px 7px !important; }
          .tp-dashboard { padding: 4px 8px !important; gap: 4px !important; }
        }
      `}</style>
      <div style={s.tabsBar}>
        <div style={s.tabGroup} className="tp-tab-group">
          {panelesVisibles.map((p) => (
            <button
              key={p.id}
              onClick={() => seleccionarTab(p)}
              style={{
                ...s.tabBtn,
                ...(activeTab === p.id ? s.tabBtnActive : {}),
                ...(activeTab !== p.id ? s.tabBtnHidden : {}),
              }}
              className={`tp-tab ${activeTab === p.id ? 'tp-tab-active' : ''}`}
            >
              {p.icono} <span className="tp-tab-label">{p.label}</span>
              {pendientesPorPanel(p) > 0 && (
                <span style={s.tabBadge}>
                  {pendientesPorPanel(p) > 9 ? '9+' : pendientesPorPanel(p)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'pedidos' ? (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {!puedeEditarPedido && (
            <div
              style={{
                padding: '10px 16px',
                margin: '0',
                borderRadius: '10px',
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                color: 'var(--agx-warning-text)',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}
            >
              <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
              pedidos.
            </div>
          )}
          <div style={s.mainArea}>
            <div style={{ ...s.fullPanel, opacity: pedidoActivo ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              <PedidosPanel
                pedidos={pedidosConNoLeidos}
                pedidoActivo={pedidoActivo}
                onSelectPedido={seleccionarPedido}
                sedeFilter={sedeFilter}
                onSedeFilterChange={setSedeFilter}
                sedes={sedes}
                puedeGestionar={puedeGestionarPedidos}
                puedeCrear={puedeCrearPedido}
              />
            </div>
            {pedidoActivo && (
              <Modal
                open={!!pedidoActivo}
                onClose={cerrarDetalle}
                width={chatActivo ? 'xl' : 'lg'}
                title={null}
                noScroll
              >
                <PedidoDetalle
                  pedido={pedidoActivo}
                  onBack={cerrarDetalle}
                  readOnly={!puedeEditarPedido}
                  embedded
                  isMobile={false}
                  chatMensajes={mensajes}
                  chatCargandoMas={cargandoMas}
                  chatHasMore={hasMore}
                  onChatCargarMas={cargarMasMensajes}
                  onChatEnviar={handleEnviarMensajeChat}
                  onChatAbrir={handleAbrirChatEnDetalle}
                  onChatToggle={setChatActivo}
                  onChatRefresh={handleChatRefresh}
                  onTomarChat={tomarChat}
                  onLiberarChat={isAdmin() ? liberarChat : undefined}
                />
              </Modal>
            )}
          </div>
        </div>
      ) : activeTab === 'delivery' ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!puedeGestionarDelivery && (
            <div
              style={{
                padding: '10px 16px',
                margin: '12px 12px 0',
                borderRadius: '10px',
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                color: 'var(--agx-warning-text)',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
              entregas.
            </div>
          )}
          <DeliveryPanel
            isReadOnly={!puedeGestionarDelivery}
            sedeFilter={sedeFilter}
            isAdmin={isAdmin()}
            deliveryCedula={usuario?.cedula}
          />
        </div>
      ) : activeTab === 'armado' ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!puedeGestionarArmado && (
            <div
              style={{
                padding: '10px 16px',
                margin: '12px 12px 0',
                borderRadius: '10px',
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                color: 'var(--agx-warning-text)',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
              armado.
            </div>
          )}
          <ArmadorPanel
            isReadOnly={!puedeGestionarArmado}
            sedeFilter={sedeFilter}
            armadorCedula={usuario?.cedula}
            esCocinero={false}
            cocineroGestionar={false}
          />
        </div>
      ) : activeTab === 'historial' ? (
        <PedidosHistorial sedeFilter={sedeFilter} />
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!cocineroGestionar && (
            <div
              style={{
                padding: '10px 16px',
                margin: '12px 12px 0',
                borderRadius: '10px',
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                color: 'var(--agx-warning-text)',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FiAlertTriangle size={14} /> Solo tienes acceso de lectura. No puedes gestionar
              cocina.
            </div>
          )}
          <ArmadorPanel
            isReadOnly={!cocineroGestionar}
            sedeFilter={sedeFilter}
            armadorCedula={usuario?.cedula}
            esCocinero={true}
            cocineroGestionar={cocineroGestionar}
          />
        </div>
      )}
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            padding: '14px 24px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: 600,
            color: '#fff',
            zIndex: 2000,
            animation: 'toastIn 0.3s ease',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            background: '#0B1A4A',
          }}
        >
          {toastMsg}
        </div>
      )}
    </div>
  );
};

export default TrabajadorPanel;
